import { useAuthContext } from '@aibees/shared/auth';
import {
  CustomPopover,
  Icon,
  IconType,
  Scrollbar,
  TextEllipsis,
  usePopover,
} from '@aibees/shared/components';
import { useUserProfileApi } from '@aibees/shared/features/user-profile/hooks/use-user-profile-api';
import { userGetFullName } from '@aibees/shared/utils';
import { useGetUserRole } from '@aibees/shared/workspace';
import { Box, Stack, Typography } from '@mui/material';
import { ReactNode, useMemo } from 'react';
import { AccountPopoverCredits } from './components/account-popover-credits';
import { AccountPopoverSubscriptionPlan } from './components/account-popover-subscription-plan';
import {
  AccountMenuOptions,
  SettingsMenuItems,
} from './components/settings-menu-items';
import * as Styled from './styles';
import * as SX from './sx';

export enum AccountMenuItemType {
  LINK = 'LINK',
  TITLE = 'TITLE',
  DIVIDER = 'DIVIDER',
}

export type AccountMenuItem =
  | {
      key?: string;
      label: ReactNode;
      linkTo?: string;
      icon?: IconType;
      type?: AccountMenuItemType;
      disabled?: boolean;
      blank?: boolean;
    }
  | 'divider';

export default function AccountPopover({ items, credit }: AccountMenuOptions) {
  const { logOut, user, userFullName } = useAuthContext();
  const { data: userData, dataUpdatedAt } = useUserProfileApi();
  const userRole = useGetUserRole();
  const popover = usePopover();

  // Use API data as primary source, fallback to token user data
  const displayName = useMemo(() => {
    return userGetFullName(userData) || userFullName || 'Unknown';
  }, [userData, userFullName]);

  const displayEmail = useMemo(() => {
    return userData?.email || user?.email || 'no email';
  }, [userData?.email, user?.email]);

  const avatarUrl = useMemo(() => {
    const url = userData?.attributes?.avatar;
    if (!url) return undefined;
    return `${url}?v=${dataUpdatedAt}`;
  }, [userData?.attributes?.avatar, dataUpdatedAt]);

  const handleLogout = async () => {
    try {
      popover.onClose();
      await logOut();
    } catch (error) {
      console.error(error);
    }
  };
  const handleClickItem = () => {
    popover.onClose();
  };

  return (
    <CustomPopover
      state={popover}
      sx={SX.accountPopover}
      contentSx={SX.popoverContent}
      element={
        <Styled.AccountPopoverButton variant="outlined" size="small">
          <Styled.Avatar
            size={24}
            src={avatarUrl}
            alt={displayName}
            variant="rounded"
            color="white"
          >
            <Icon size={20} name={'person-outline'} color="grey.400" />
          </Styled.Avatar>
          <Icon
            sxx={SX.popupIcon}
            name={!popover.open ? 'keyboard-arrow-down' : 'keyboard-arrow-up'}
            size={16}
          />
        </Styled.AccountPopoverButton>
      }
    >
      <Box sx={SX.popoverTitle}>
        <Styled.AccountPopoverTitleWrapper>
          <TextEllipsis
            value={displayName}
            w="30ch"
            variant="body3"
            fontWeight={600}
            color="grey.700"
          />
          <Stack direction="row" gap={0.5}>
            <Typography
              noWrap
              variant="body4"
              fontWeight={500}
              color="grey.500"
              textTransform="capitalize"
            >
              {userRole.toLocaleLowerCase()} |
            </Typography>
            <TextEllipsis
              variant="body4"
              fontWeight={500}
              color="grey.500"
              w="25ch"
              value={displayEmail}
            />
          </Stack>
        </Styled.AccountPopoverTitleWrapper>
      </Box>
      {credit || (
        <Stack sx={SX.creditBox}>
          <Styled.CreditInfo>
            <AccountPopoverCredits />
          </Styled.CreditInfo>
          <AccountPopoverSubscriptionPlan />
        </Stack>
      )}

      <Stack sx={{ mt: 1 }}>
        <Scrollbar
          className="AccountPopoverScrollBar"
          classNames={{
            contentEl: 'AccountPopoverScrollBar_Content',
            contentWrapper: 'AccountPopoverScrollBar_ContentWrapper',
          }}
          sx={{
            '@media (min-height: 1000px)': {
              '& .simplebar-wrapper': {
                height: 'auto',
              },
            },
            '& .AccountPopoverScrollBar_Content': {
              flexGrow: 1,
            },
            '& .AccountPopoverScrollBar_ContentWrapper': {
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              '::-webkit-scrollbar': {
                display: 'none',
              },
            },
          }}
        >
          {/* <SwitchModeMenuItem /> */}
          <SettingsMenuItems items={items} onClickItem={handleClickItem} />
          <Styled.MenuItemLogout onClick={handleLogout}>
            <Icon name={'logout'} color="error.500" size={20} />
            Logout
          </Styled.MenuItemLogout>
        </Scrollbar>
      </Stack>
    </CustomPopover>
  );
}
